import { Divider } from '@outdoorsyco/bonfire';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { useIntl } from 'react-intl';
import clip from 'text-clipper';

import { IArticleTagsAndCategories, IPageCategory } from '@/services/getHelpPageData';
import { stripAndDecodeHtml } from '@/utility/stripHtml';

import ArticleCategories from '../static-pages/help/ArticleCategories';
import Articles from '../static-pages/help/Articles';
import HeadMeta from '../static-pages/help/HeadMeta';
import Hero from '../static-pages/help/Hero';
import Tags from '../static-pages/help/Tags';
import css from './HelpTemplate.module.css';

interface IProps {
  title: string;
  slug: string;
  articles: IPageCategory[];
  articleCategories: IArticleTagsAndCategories[];
  helpCategories: IPageCategory[];
  searchQuery?: string;
}
interface IArticleCategoriesProps {
  articleCategories: IArticleTagsAndCategories[];
}

const ArticleMainCategories: React.FC<IArticleCategoriesProps> = ({ articleCategories }) => {
  const intl = useIntl();
  const TAGS = intl.formatMessage({ defaultMessage: 'TAGS', id: 'yJWdDO' });
  const CATEGORIES = intl.formatMessage({ defaultMessage: 'CATEGORIES', id: 'kW0X61' });
  const READ_MORE = intl.formatMessage({ defaultMessage: 'read more', id: '0lF7Yp' });

  if (!articleCategories || !articleCategories.length) {
    return null;
  }

  return (
    <>
      {articleCategories.map(article => {
        const hasTags = article.tags && article.tags.length > 0;
        const hasCategoryTags = article.categories && article.categories.length > 0;
        const CHAR_LIMIT = 140;
        let readMore = false;
        const escapedContent = article?.content && stripAndDecodeHtml(article.content);
        const articleSnippet = escapedContent
          ? clip(`<p>${escapedContent}</p>`, CHAR_LIMIT, {
              html: true,
              maxLines: 3,
            })
          : '';
        if (article?.content?.length >= CHAR_LIMIT) {
          readMore = true;
        }

        return (
          <div key={article.id}>
            <div className="mt-4 mb-2">
              <a
                dangerouslySetInnerHTML={{ __html: article.title || '' }}
                className="text-green-600 h4"
                href={`/help/${article.slug}`}
              />
            </div>
            <div
              className={`${css.content} ${css.categoriesContent}`}
              dangerouslySetInnerHTML={{ __html: articleSnippet }}
            />
            {readMore && (
              <a
                className={'body-sm mb-4 underline hover:no-underline'}
                href={`/help/${article.slug}`}>
                {READ_MORE}
              </a>
            )}
            <div className="my-6">
              {hasTags && (
                <div className="flex my-2">
                  <div className={'sm-caps-3 py-2'}>{TAGS}</div>
                  <div className="flex-auto">
                    <Tags tags={article.tags} categoryTag />
                  </div>
                </div>
              )}

              {hasCategoryTags && (
                <div className="flex my-2">
                  <div className={'sm-caps-3 py-2'}>{CATEGORIES}</div>
                  <div className="flex-auto">
                    <Tags tags={article.categories} />
                  </div>
                </div>
              )}
            </div>

            <Divider className={'my-9'} />
          </div>
        );
      })}
    </>
  );
};

const HelpCategoriesTemplate = React.memo<IProps>(
  ({ helpCategories, title, articles, articleCategories, slug, searchQuery }) => {
    const intl = useIntl();
    const SEARCH_RESULTS_FOR = intl.formatMessage({
      defaultMessage: 'Search Results for',
      id: 'pF56hV',
    });
    const NO_RESULTS = intl.formatMessage({
      defaultMessage: 'No search results found for',
      id: '+ozuSS',
    });
    const HELP_CATEGORIES = intl.formatMessage({ defaultMessage: 'Help Categories', id: 'meWUOO' });
    const POPULAR_ARTICLES = intl.formatMessage({
      defaultMessage: 'Popular Articles',
      id: 'G3D4NH',
    });
    return (
      <>
        <HeadMeta title={title} slug={slug} />
        <Hero />
        <div className="mx-auto xs:px-4 sm:px-8 md:px-12 lg:px-16 max-w-74">
          {!searchQuery && <h3 className="mt-20 mb-10 text-gray-900 h3">{title}</h3>}

          {searchQuery && isEmpty(articleCategories) ? (
            <h3 className="mt-20 mb-10 text-gray-900 h3">
              {NO_RESULTS}: {searchQuery}
            </h3>
          ) : (
            <>
              {searchQuery && (
                <h3 className="mt-20 mb-10 h3">
                  {SEARCH_RESULTS_FOR}: {searchQuery}
                </h3>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-y-4 md:gap-x-20">
                <div className={'md:col-span-2 mb-10'}>
                  <ArticleMainCategories articleCategories={articleCategories} />
                </div>

                <div className="mb-10 max-w-17">
                  {articleCategories && articleCategories.length ? (
                    <>
                      <h6 className="mb-2 h6">{HELP_CATEGORIES}</h6>
                      <ArticleCategories articleCategories={helpCategories} />
                      <Divider className={'my-9'} />
                    </>
                  ) : null}
                  {articles && articles.length ? (
                    <>
                      <h6 className="py-4  h6">{POPULAR_ARTICLES}</h6>
                      <Articles articles={articles} />
                    </>
                  ) : null}
                </div>
                <div id="help" />
              </div>
            </>
          )}
        </div>
      </>
    );
  },
);
HelpCategoriesTemplate.displayName = 'HelpCategoriesTemplate';
export default HelpCategoriesTemplate;
